import {
  CommunicationLevel,
  EmailChannel,
  EmailChannelType,
  Prisma,
  prisma,
  ResourceType,
  ActionType,
} from "client";

// CREATE
export const createEmailChannel = async (
  userId: string,
  data: EmailChannel
) => {
  try {
    const emailChannel = await prisma.emailChannel.create({
      data: {
        communicationChannel: { connect: { id: data.communicationChannelId } },
        type: data.type,
      },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};

/** Create email channel with SendGrid. Use entityId + level for WL, Organization, Event, etc. */
export const createEmailChannelWithSendGrid = async (
  userId: string,
  entityId: string,
  sendGridApiKey: string,
  senderEmail: string,
  communicationChannelId?: string,
  fromName?: string,
  level: CommunicationLevel = CommunicationLevel.WHITE_LABEL
) => {
  try {
    let connect: Record<string, { connect: { id: string } }> = {};
    switch (level) {
      case CommunicationLevel.WHITE_LABEL:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EVENT:
        connect = { event: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.ORGANIZATION:
        connect = { organization: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EXHIBITOR:
        connect = { exhibitor: { connect: { id: entityId } } };
        break;
      default:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
    }
    const communicationChannel = communicationChannelId
      ? null
      : await prisma.communicationChannel.create({
          data: {
            level,
            ...connect,
          },
        });
    const emailChannel = await prisma.emailChannel.create({
      data: {
        communicationChannel: {
          connect: { id: communicationChannel?.id || communicationChannelId! },
        },
        type: EmailChannelType.SEND_GRID,
        sendGridChannel: {
          create: {
            apiKey: sendGridApiKey,
            senderEmail,
            fromName,
          },
        },
      },
    });
    return { emailChannel };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const createEmailChannelWithNodeMailer = async (
  entityId: string,
  level: CommunicationLevel,
  nodeMailerData: Prisma.NodeMailerChannelCreateInput,
  communicationChannelId?: string,
  fromName?: string
) => {
  try {
    let connect = {};
    switch (level) {
      case CommunicationLevel.WHITE_LABEL:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EVENT:
        connect = { event: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.ORGANIZATION:
        connect = { organization: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EXHIBITOR:
        connect = { exhibitor: { connect: { id: entityId } } };
        break;

      default:
        break;
    }
    const communicationChannel = communicationChannelId
      ? null
      : await prisma.communicationChannel.create({
          data: {
            level,
            ...connect,
          },
        });
    const emailChannel = await prisma.emailChannel.create({
      data: {
        communicationChannel: {
          connect: { id: communicationChannel?.id || communicationChannelId },
        },
        type: EmailChannelType.NODE_MAILER,
        nodeMailerChannel: {
          create: {
            host: nodeMailerData.host,
            port: nodeMailerData.port,
            secure: nodeMailerData.secure,
            authUser: nodeMailerData.authUser,
            authPass: nodeMailerData.authPass,
            senderEmail: nodeMailerData.senderEmail,
          },
        },
      },
    });

    return { emailChannel };
  } catch (error) {
    return { error };
  }
};
// READ
export const getAllEmailChannels = async (
  userId: string
): Promise<{
  emailChannels?: EmailChannel[];
  error?: any;
}> => {
  try {
    const emailChannels = await prisma.emailChannel.findMany();
    return { emailChannels };
  } catch (error) {
    return { error };
  }
};

export const getEmailChannelById = async (userId: string, id: string) => {
  try {
    const emailChannel = await prisma.emailChannel.findUnique({
      where: { id },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};

export const getEmailChannelByCommuncationChannelsId = async (
  userId: string,
  communicationChannelId: string
) => {
  try {
    const emailChannel = await prisma.emailChannel.findUnique({
      where: { communicationChannelId },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};

export const getCommunicationChannelDataByWhiteLabelId = async (
  entityId: string
) => {
  try {
    const communicationChannel = await prisma.communicationChannel.findUnique({
      where: { levelEntityID: entityId },
      include: {
        emailChannel: {
          include: {
            sendGridChannel: true,
          },
        },
      },
    });
    return communicationChannel;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEmailChannelDataByWhiteLabelId = async (
  entityId: string,
  userId: string
) => {
  try {
    const communicationChannel = await prisma.communicationChannel.findUnique({
      where: { levelEntityID: entityId },
    });
    const emailChannel = await prisma.emailChannel.findUnique({
      where: { communicationChannelId: communicationChannel?.id },
    });
    return emailChannel;
  } catch (error) {
    return { error };
  }
};

export const getEmailChannelDataByCommuncationChannelsIdWithSendGrid = async (
  userId: string,
  communicationChannelId?: string
) => {
  try {
    const emailChannel = await prisma.emailChannel.findUnique({
      where: { communicationChannelId },
      include: {
        sendGridChannel: true,
        nodeMailerChannel: true,
      },
    });
    return emailChannel;
  } catch (error) {
    return { error };
  }
};

// UPDATE
export const updateEmailChannelById = async (
  userId: string,
  id: string,
  data: EmailChannel
) => {
  try {
    const emailChannel = await prisma.emailChannel.update({
      where: { id },
      data: {
        communicationChannel: { connect: { id: data.communicationChannelId } },
        type: data.type,
      },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};

export const updateEmailChannelByIdWithSendGrid = async (
  userId: string,
  id: string,
  apiKey: string | undefined,
  senderEmail: string,
  fromName?: string
) => {
  try {
    const emailChannel = await prisma.emailChannel.update({
      where: { id },
      data: {
        sendGridChannel: {
          update: {
            ...(apiKey !== undefined && { apiKey }),
            senderEmail,
            fromName,
          },
        },
      },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};

export const updateEmailChannelByIdWithNodeMailer = async (
  id: string,
  nodeMailerData: any
) => {
  try {
    const emailChannel = await prisma.emailChannel.update({
      where: { id },
      data: {
        nodeMailerChannel: {
          upsert: {
            create: nodeMailerData,
            update: nodeMailerData,
          },
        },
      },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};
// DELETE

export const deleteEmailChannelById = async (userId: string, id: string) => {
  try {
    const emailChannel = await prisma.emailChannel.delete({
      where: { id },
    });
    return { emailChannel };
  } catch (error) {
    return { error };
  }
};
